import { getActionDisplay } from '../featureFormConstants';
import { SOVEREIGN } from '../../../epc/utils/epcProjectVisual';
import type { ActionCatalogEntry } from '../featureFormConstants';

export function PermissionCrudScope({
  moduleName,
  primaryAction,
  scopeActions,
  actionCatalog,
  isLoading,
  onPrimaryChange,
  onScopeToggle,
}: {
  moduleName: string;
  primaryAction: string;
  scopeActions: string[];
  actionCatalog: ActionCatalogEntry[];
  isLoading: boolean;
  onPrimaryChange: (action: string) => void;
  onScopeToggle: (action: string) => void;
}) {
  return (
    <div className="space-y-3">
      {/* What this maps to */}
      {moduleName && (
        <div
          className="flex items-center gap-2 p-2.5 rounded-lg"
          style={{
            background: 'rgba(31,90,71,0.06)',
            border: `1px solid ${SOVEREIGN.cardBorder}`,
          }}
        >
          <span
            className="text-xs font-mono font-bold px-2 py-0.5 rounded"
            style={{ background: 'rgba(31,90,71,0.12)', color: SOVEREIGN.bottle }}
          >
            permissionKey:
          </span>
          <span
            className="text-sm font-mono font-semibold"
            style={{ color: SOVEREIGN.bottle }}
          >
            {moduleName}.{primaryAction || 'read'}
          </span>
          <span
            className="text-xs ml-auto"
            style={{ color: SOVEREIGN.kpiCaption }}
          >
            → used for menu visibility check
          </span>
        </div>
      )}

      {/* Actions grid */}
      <div>
        <p
          className="text-xs font-semibold mb-2"
          style={{ color: SOVEREIGN.ink }}
        >
          Select primary action (menu access) and tick which CRUD operations this module supports in roles:
        </p>
        {isLoading ? (
          <div
            className="flex items-center gap-2 px-3 py-4 text-xs rounded-xl"
            style={{
              color: SOVEREIGN.kpiCaption,
              background: SOVEREIGN.paper,
              border: `1px solid ${SOVEREIGN.cardBorder}`,
            }}
          >
            <span
              className="inline-block w-3 h-3 rounded-full animate-spin"
              style={{ borderWidth: 2, borderStyle: 'solid', borderColor: `${SOVEREIGN.barTrack} ${SOVEREIGN.barTrack} ${SOVEREIGN.brand} ${SOVEREIGN.barTrack}` }}
            />
            Loading action catalog…
          </div>
        ) : actionCatalog.length === 0 ? (
          <div
            className="px-3 py-4 text-xs rounded-xl"
            style={{
              color: SOVEREIGN.kpiCaption,
              background: SOVEREIGN.paper,
              border: `1px solid ${SOVEREIGN.cardBorder}`,
            }}
          >
            No actions available from the server. Defaulting to <code className="font-mono">read</code>.
          </div>
        ) : (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {actionCatalog.map(action => {
            const display = getActionDisplay(action.key);
            const isPrimary = primaryAction === action.key;
            const isInScope = scopeActions.includes(action.key);

            return (
              <div
                key={action.key}
                className="relative rounded-xl p-2.5 transition-all"
                style={
                  isPrimary
                    ? {
                        border: `2px solid ${SOVEREIGN.brand}`,
                        background: 'rgba(51,146,116,0.07)',
                        boxShadow: `0 2px 6px ${SOVEREIGN.brand}22`,
                      }
                    : isInScope
                      ? {
                          border: `2px solid ${SOVEREIGN.champagne}`,
                          background: 'rgba(201,169,97,0.07)',
                        }
                      : {
                          border: `2px solid ${SOVEREIGN.cardBorder}`,
                          background: SOVEREIGN.cardBg,
                        }
                }
              >
                {/* Primary radio */}
                <div className="flex items-start gap-2">
                  <div className="flex flex-col gap-1.5 flex-1">
                    <div className="flex items-center gap-1.5">
                      <span className="text-sm">{display.emoji}</span>
                      <span
                        className="text-xs font-bold"
                        style={{
                          color: isPrimary ? SOVEREIGN.bottle : isInScope ? SOVEREIGN.champagne : SOVEREIGN.ink,
                        }}
                      >
                        {action.name}
                      </span>
                    </div>
                    <p
                      className="text-xs leading-tight"
                      style={{ color: SOVEREIGN.kpiCaption }}
                    >
                      {action.description}
                    </p>
                  </div>
                </div>

                {/* Two controls: set as primary | include in scope */}
                <div
                  className="flex items-center gap-2 mt-2 pt-2"
                  style={{ borderTop: `1px solid ${SOVEREIGN.innerRule}` }}
                >
                  <button
                    type="button"
                    onClick={() => onPrimaryChange(action.key)}
                    title="Set as primary access key"
                    className="flex-1 text-xs py-0.5 rounded-md font-medium transition-colors"
                    style={
                      isPrimary
                        ? { background: SOVEREIGN.bottle, color: '#fff' }
                        : { background: SOVEREIGN.paper, color: SOVEREIGN.kpiCaption }
                    }
                  >
                    {isPrimary ? '★ Primary' : '☆ Primary'}
                  </button>
                  <button
                    type="button"
                    onClick={() => onScopeToggle(action.key)}
                    title="Include in role permission scope"
                    className="flex-1 text-xs py-0.5 rounded-md font-medium transition-colors"
                    style={
                      isInScope
                        ? { background: SOVEREIGN.champagne, color: SOVEREIGN.title }
                        : { background: SOVEREIGN.paper, color: SOVEREIGN.kpiCaption }
                    }
                  >
                    {isInScope ? '✓ In Roles' : '+ In Roles'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
        )}
        <p
          className="text-xs mt-2 flex items-center gap-1"
          style={{ color: SOVEREIGN.kpiCaption }}
        >
          <span>★ Primary</span> — permission key for menu visibility.
          <span className="ml-2">✓ In Roles</span> — actions available in role permission matrix.
        </p>
      </div>

      {/* Scope summary */}
      {scopeActions.length > 0 && (
        <div
          className="p-2.5 rounded-lg"
          style={{
            background: SOVEREIGN.inputBg,
            border: `1px solid ${SOVEREIGN.cardBorder}`,
          }}
        >
          <p
            className="text-xs font-semibold mb-1.5"
            style={{ color: SOVEREIGN.ink }}
          >
            Role permission preview for{' '}
            <code
              className="font-mono"
              style={{ color: SOVEREIGN.bottle }}
            >
              {moduleName || 'module'}
            </code>:
          </p>
          <div className="flex flex-wrap gap-1.5">
            {scopeActions.map(a => {
              const display = getActionDisplay(a);
              const cat = actionCatalog.find(x => x.key === a);
              return (
                <span key={a} className={`text-xs px-2 py-0.5 rounded-full font-medium border ${display.color}`}>
                  {display.emoji} {cat?.name ?? a}
                </span>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
